import { useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CircularProgress,
    Container,
    Divider,
    IconButton,
    InputAdornment,
    Link,
    TextField,
    Typography,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { changePassword } from "../../../app/features/sessions/sessionSlice";
import { AppDispatch, RootState } from "../../../app/store";
import * as REQUEST_REQUIREMENTS from '../../../app/api/requestRequirements';
import jinnerLogo from '../../../app/assets/logo-white-name-white-without-background-portrait.png';

const BG = '#15151a';
const CARD_BG = '#1c1c24';
const BRAND_RED = '#c0392b';
const TEXT = '#f4f4f6';
const TEXT_SECONDARY = 'rgba(244,244,246,0.6)';
const BORDER = 'rgba(255,255,255,0.12)';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        color: TEXT,
        '& fieldset': { borderColor: BORDER },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 100px ${CARD_BG} inset`,
            WebkitTextFillColor: TEXT,
            caretColor: TEXT,
        },
        '&.Mui-focused fieldset': { borderColor: 'rgba(255,255,255,0.7)' },
    },
    '& .MuiInputLabel-root': { color: TEXT_SECONDARY },
    '& .MuiInputLabel-root.Mui-focused': { color: TEXT, fontWeight: 700 },
    '& .MuiInputAdornment-root .MuiIconButton-root': { color: TEXT_SECONDARY },
};

export function NewPassword() {
    const [searchParams] = useSearchParams();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const loading = useSelector((state: RootState) => state.session.loading);

    const [password, setPassword] = useState('');
    const [passwordConfirmation, setPasswordConfirmation] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorMessages, setErrorMessages] = useState<string[]>([]);
    const [successMessage, setSuccessMessage] = useState('');

    const resetPasswordToken = searchParams.get('reset_password_token') ?? '';

    async function handleConfirm() {
        if (!password || !passwordConfirmation) {
            setErrorMessages(['Please fill in both password fields.']);
            return;
        }
        if (password !== passwordConfirmation) {
            setErrorMessages(['Passwords do not match.']);
            return;
        }

        setErrorMessages([]);
        setSuccessMessage('');

        const response = await dispatch(changePassword({
            password,
            passwordConfirmation,
            resetPasswordToken,
        }));

        const payload = response.payload as any;

        if (payload?.success === true) {
            setSuccessMessage(payload.message ?? 'Password changed successfully.');
            setPassword('');
            setPasswordConfirmation('');
        } else {
            const errors = payload?.errors?.full_messages ?? payload?.errors ?? ['An error occurred. Please try again.'];
            setErrorMessages(Array.isArray(errors) ? errors : [errors]);
        }
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: BG, display: 'flex', alignItems: 'center' }}>
            <Container maxWidth="sm">
                <Card sx={{
                    bgcolor: CARD_BG,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 3,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                }}>
                    <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <Box component="img" src={jinnerLogo} alt="Jinner" sx={{ height: 80 }} />
                        </Box>

                        <Typography variant="h5" fontWeight={700} color={TEXT} sx={{ mb: 3 }}>
                            Change Password
                        </Typography>

                        {errorMessages.length > 0 && (
                            <Alert severity="error" sx={{ mb: 2 }} aria-live="assertive">
                                {errorMessages.map((e, i) => <div key={i}>{e}</div>)}
                            </Alert>
                        )}

                        {successMessage && (
                            <Alert severity="success" sx={{ mb: 2 }} aria-live="assertive">
                                {successMessage}
                            </Alert>
                        )}

                        <TextField
                            label="New Password"
                            type={showPassword ? 'text' : 'password'}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            sx={fieldSx}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword((v) => !v)} edge="end">
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            label="Confirm New Password"
                            type="password"
                            value={passwordConfirmation}
                            onChange={(e) => setPasswordConfirmation(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && !loading && handleConfirm()}
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            sx={fieldSx}
                        />

                        <Button
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            onClick={handleConfirm}
                            sx={{
                                mt: 3,
                                py: 1.4,
                                bgcolor: BRAND_RED,
                                '&:hover': { bgcolor: '#a93226' },
                                '&.Mui-disabled': { bgcolor: 'rgba(192,57,43,0.4)' },
                            }}
                        >
                            {loading ? <CircularProgress size={22} color="inherit" /> : 'Confirm'}
                        </Button>
                    </CardContent>

                    <Divider sx={{ borderColor: BORDER }} />

                    <CardActions sx={{ justifyContent: 'center', py: 2 }}>
                        <Typography variant="body2" sx={{ color: TEXT_SECONDARY }}>
                            Remember your password?{' '}
                            <Link
                                component="button"
                                onClick={() => navigate(REQUEST_REQUIREMENTS.SIGNIN_ENDPOINT)}
                                sx={{ color: BRAND_RED, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                            >
                                Sign in
                            </Link>
                        </Typography>
                    </CardActions>
                </Card>
            </Container>
        </Box>
    );
}
